import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useMyShiftDetailsForDay } from '@/services/employee/useShiftDetailsForDay';
import { FunctionComponent, Suspense, useMemo } from 'react';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import ShiftRow from './ShiftRow';
import { formatTimelineTime, useBuildShiftTimeline } from './useBuildShiftTimeline';
import { ViewShiftDetailsHeader } from './ViewShiftDetailsHeader';

const ViewShiftDetails: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const selectedDate = useMemo(() => new Date(date), [date]);
  const data = useMyShiftDetailsForDay(selectedDate);
  const onChangeDate = (newDate: Date) => {
    navigate(`/ess/my-calendar/${dateToApiFormat(newDate)}/shift-details`, { replace: true });
  };

  const timelineItems = useBuildShiftTimeline(data);

  return (
    <PageContainer customHeader={<ViewShiftDetailsHeader />}>
      <div className="bg-white border-b-2">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={selectedDate} onChange={onChangeDate} />
        </PageSizer>
      </div>
      <Suspense fallback={<TableLoader />}>
        <div className="overflow-y-hidden flex flex-col">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            {data ? (
              <div className="space-y-4">
                <div className="border-2 rounded-md border-primary-500 p-2 text-primary-500 bg-white shadow-sm">
                  <div className="flex justify-center items-center gap-1">
                    <HiOutlineLocationMarker className="w-5 h-5" />
                    <span className="text-sm font-semibold">{data.location?.name}</span>
                  </div>
                </div>
                <div className="flex flex-col space-y-4">
                  {timelineItems.map(item => (
                    <ShiftRow
                      key={`${item.type}-${item.label}-${formatTimelineTime(item)}`}
                      time={formatTimelineTime(item)}
                      label={item.label}
                      icon={item.icon}
                      bgColor={item.bgColor}
                      textColor={item.textColor}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <p className="font-semibold">No shift details available</p>
            )}
          </PageSizer>
        </div>
      </Suspense>
    </PageContainer>
  );
};

export default ViewShiftDetails;
